import * as React from 'react';

import 'dayjs/locale/ja';
import { FormHelperText, TextField, Typography, TextFieldProps } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { PickerChangeHandlerContext, DateValidationError } from '@mui/x-date-pickers';

import { gray100, gray80, gray60, red50 } from '../../../constants/colors';
import {
  formLabelWrapper,
  formNarrowMb,
  formExtraNarrowMb,
  formWideMb,
  baseTypography,
  basePalette,
  baseButtonHoverStyle,
} from '../../../constants/location';

import type { SxProps } from '@mui/material';
import type { FieldError, FieldErrorsImpl, Merge } from 'react-hook-form';
import dayjs from 'dayjs';

interface Props {
  labelText?: string;
  requiredText?: string;
  isRequired: boolean;
  error?: boolean;
  helperText?: string | FieldError | Merge<FieldError, FieldErrorsImpl<Error>> | undefined;
  fieldType?: string;
  width?: string;
  spWidth?: string;
  formMb?: string | undefined;
  placeholder?: string;
  disabled?: boolean;
  disabledColor?: string;
  /** 値を変更できないが、フォーカス・選択・コピーはできる状態にする（計算フィールド等）（#3172） */
  readOnly?: boolean;
  value: Date | null | undefined;
  onChange: (value: Date | null, context: PickerChangeHandlerContext<DateValidationError>) => void;
  flex?: number;
  twoComponent?: boolean;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  height?: string;
  heightIs40px?: boolean;
  paddingIs10px8px?: boolean;
  fontSizeIs14px?: boolean;
  labelFontSize?: string; // ラベルのフォントサイズ（未指定は 12px）
  hideHelperText?: boolean;
  dateFormat?: string; // 日付出力形式（例: "YYYY/MM/DD", "YYYY年MM月DD日"）
}

const CustomDatePicker = React.forwardRef(
  (props: Props, _ref): React.ReactElement | JSX.Element => {
    const locale = 'ja' as const;
    const {
      labelText,
      requiredText,
      isRequired,
      error,
      helperText,
      formMb,
      width,
      disabled,
      disabledColor,
      readOnly,
      value,
      flex,
      twoComponent,
      onChange,
      onFocus,
      onBlur,
      height,
      heightIs40px,
      paddingIs10px8px,
      fontSizeIs14px,
      labelFontSize,
      hideHelperText,
      dateFormat,
      ...rest
    } = props;

    const theme = createTheme({
      typography: baseTypography,
      palette: basePalette,
      components: {
        // ボタン・アイコンボタンの既定ホバーはグレー（#3310）
        ...baseButtonHoverStyle,
        MuiOutlinedInput: {
          styleOverrides: {
            root: {
              '& .MuiSvgIcon-root': {
                color: disabled && disabledColor ? gray100 : 'inherit',
              },
            },
          },
        },
      },
    });

    let inputWidth;
    let inputSpWidth;
    if (width === 'search') {
      inputWidth = '152px';
      inputSpWidth = '134px';
    } else {
      inputWidth = width;
      inputSpWidth = width;
    }

    const styles = {
      datePicker: {
        '& .MuiOutlinedInput-root': {
          '& fieldset': {
            borderColor: error ? red50 : gray60,
            borderWidth: '1px',
          },
          '&.Mui-focused fieldset': {
            borderColor: error ? red50 : gray80,
            borderWidth: '1px',
          },
          '&:hover fieldset': {
            borderWidth: '1px',
          },
          ...(height ? { height } : heightIs40px ? { height: '40px' } : {}),
        },
        '& .MuiInputBase-input.MuiOutlinedInput-input': {
          padding: paddingIs10px8px ? '10px 8px' : '16.5px 14px',
          fontSize: fontSizeIs14px ? '14px' : '16px',
        },
        '@media screen and (max-width:600px)': {
          width: inputSpWidth ?? '148px',
        },
        width: inputWidth ?? '180px',
      },
      formLabelWrapper,
      label: {
        fontSize: labelFontSize ?? '12px',
        fontWeight: '400',
      },
      required: {
        color: red50,
        fontSize: labelFontSize ?? '12px',
        marginLeft: '10px',
      },
      inputBox: {
        hight: '51px',
        padding: '16px 12px',
      },
    };

    const inputformMb = (marginBottom: string | undefined): SxProps => {
      if (marginBottom === 'zero') return formExtraNarrowMb;
      if (marginBottom === 'narrow') return formNarrowMb;
      return formWideMb;
    };

    const textfieldHelper = (): string | undefined => {
      if (helperText === undefined) return;
      return String(helperText);
    };

    return (
      <Typography flex={flex} sx={inputformMb(formMb)} align="left" component="div">
        {labelText && (
          <Typography align="left" sx={styles.formLabelWrapper} component="div">
            <Typography color={gray80} sx={styles.label} component="span">
              {labelText}
            </Typography>
            {isRequired && (
              <Typography component="span" sx={styles.required}>
                {requiredText}
              </Typography>
            )}
          </Typography>
        )}
        <ThemeProvider theme={theme}>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
            <DatePicker
              orientation="portrait"
              slots={{
                textField: TextField,
              }}
              slotProps={{
                textField: (params: TextFieldProps) => ({
                  ...params,
                  inputProps: {
                    ...params.inputProps,
                    disabled,
                    style: {
                      ...params.inputProps?.style,
                      ...(disabled && disabledColor ? { WebkitTextFillColor: gray100 } : {}),
                      ...(value === null ? { color: gray60 } : {}),
                    },
                  },
                  size: 'small',
                  sx: styles.datePicker,
                  onFocus: onFocus,
                  onBlur: onBlur,
                }),
              }}
              format={dateFormat || 'YYYY/MM/DD'}
              onChange={(newValue, context): void => {
                onChange(newValue ? newValue.toDate() : null, context);
              }}
              value={value ? dayjs(value) : null}
              disabled={disabled}
              readOnly={readOnly}
              {...rest}
            />
          </LocalizationProvider>
        </ThemeProvider>
        {!hideHelperText &&
          (twoComponent ? (
            <Typography sx={{ height: '20px' }}>
              <FormHelperText error={error}>{textfieldHelper()}</FormHelperText>
            </Typography>
          ) : (
            <FormHelperText error={error}>{textfieldHelper()}</FormHelperText>
          ))}
      </Typography>
    );
  },
);
CustomDatePicker.displayName = 'CustomDatePicker';
export default CustomDatePicker;
